/**
 * X Ads — the connection the screens work on.
 *
 * A company can connect several X users (live and sandbox). The one in use is remembered per
 * browser (localStorage, guarded — storage can be disabled); without a choice it is the
 * company's default connection that has an ads account picked. The list is fetched once and
 * shared by the shell and the pages; the Account page refreshes it.
 */
import { useEffect, useState } from 'react';
import { errMsg, xAdsService, type XAccount } from '@/services/x-ads.service';

const ACTIVE_KEY = 'slightor.xAds.activeAccount';

let cache: XAccount[] | null = null;
let inflight: Promise<XAccount[]> | null = null;
const listeners = new Set<() => void>();
const notify = () => listeners.forEach((l) => l());

function readActiveUid(): string | null { try { return localStorage.getItem(ACTIVE_KEY); } catch { return null; } }
function writeActiveUid(uid: string): void { try { localStorage.setItem(ACTIVE_KEY, uid); } catch { /* storage unavailable */ } }

/** A connection that can run campaigns: active, with an ads account picked. */
export const isUsable = (a: XAccount): boolean => a.isActive && !!a.adsAccountId;

export function pickActive(list: XAccount[], stored = readActiveUid()): XAccount | null {
  const live = list.filter((a) => a.isActive);
  return live.find((a) => a.accountUid === stored && a.adsAccountId)
    ?? live.find((a) => a.isDefault && a.adsAccountId)
    ?? live.find((a) => a.adsAccountId)
    ?? live.find((a) => a.accountUid === stored)
    ?? live.find((a) => a.isDefault)
    ?? live[0]
    ?? null;
}

export function loadXAccounts(force = false): Promise<XAccount[]> {
  if (cache && !force) return Promise.resolve(cache);
  if (!inflight) {
    inflight = xAdsService.list().then((list) => { cache = list; return list; }).finally(() => { inflight = null; });
  }
  return inflight;
}

/** The Account page calls this after connect / select / disconnect so every screen re-reads. */
export function forgetXConnection(): void { cache = null; notify(); }

export function setActiveXAccount(uid: string): void { writeActiveUid(uid); notify(); }

export function useXConnection() {
  const [accounts, setAccounts] = useState<XAccount[] | null>(cache);
  const [error, setError] = useState<string | null>(null);
  const [revision, setRevision] = useState(0);

  useEffect(() => {
    const onChange = () => setRevision((r) => r + 1);
    listeners.add(onChange);
    return () => { listeners.delete(onChange); };
  }, []);

  useEffect(() => {
    let current = true;
    loadXAccounts()
      .then((list) => { if (current) { setAccounts(list); setError(null); } })
      .catch((e) => { if (current) setError(errMsg(e, 'Unable to load your X connections.')); });
    return () => { current = false; };
  }, [revision]);

  const account = accounts ? pickActive(accounts) : null;
  return {
    accounts,
    /** The connection in use (may have no ads account yet — check `isUsable`). */
    account,
    /** Its uid when it can run campaigns, else ''. */
    uid: account && isUsable(account) ? account.accountUid : '',
    error,
    loading: accounts === null && !error,
    reload: forgetXConnection,
    setActive: setActiveXAccount,
  };
}
